
import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, Filter, ChevronDown } from 'lucide-react'
import { Button, ButtonVariant, ButtonStyle, H2, Body, MultiSelectButton } from '@meiuca/ui-components'

export interface GameFilters {
  modalidade: string[]
  dataPreset: 'hoje' | 'amanha' | 'esta-semana' | 'customizada' | ''
  dataCustomizada: string
  nivel: string[]
  ordenacao: 'data_asc' | 'data_desc'
}

interface GameFiltersModalProps {
  isOpen: boolean
  onClose: () => void
  onApplyFilters: (filters: GameFilters) => void
  initialFilters?: Partial<GameFilters>
}

const modalidades = [
  'Futebol',
  'Basquete',
  'Vôlei',
  'Tênis',
  'Futsal',
  'Beach Tennis',
  'Padel',
  'Corrida'
]

const niveis = [
  'Iniciante',
  'Intermediário',
  'Avançado',
  'Profissional'
]

export function GameFiltersModal({ isOpen, onClose, onApplyFilters, initialFilters }: GameFiltersModalProps) {
  const [filters, setFilters] = useState<GameFilters>({
    modalidade: initialFilters?.modalidade || [],
    dataPreset: initialFilters?.dataPreset || '',
    dataCustomizada: initialFilters?.dataCustomizada || '',
    nivel: initialFilters?.nivel || [],
    ordenacao: initialFilters?.ordenacao || 'data_asc'
  })

  const handleModalidadeChange = (modalidade: string) => {
    setFilters(prev => ({
      ...prev,
      modalidade: prev.modalidade.includes(modalidade)
        ? prev.modalidade.filter(m => m !== modalidade)
        : [...prev.modalidade, modalidade]
    }))
  }

  const handleNivelChange = (nivel: string) => {
    setFilters(prev => ({
      ...prev,
      nivel: prev.nivel.includes(nivel)
        ? prev.nivel.filter(n => n !== nivel)
        : [...prev.nivel, nivel]
    }))
  }

  const handleDatePreset = (preset: GameFilters['dataPreset']) => {
    setFilters(prev => ({
      ...prev,
      dataPreset: preset,
      dataCustomizada: preset === 'customizada' ? prev.dataCustomizada : ''
    }))
  }

  const handleApplyFilters = () => {
    onApplyFilters(filters)
    onClose()
  }

  const resetFilters = () => {
    setFilters({
      modalidade: [],
      dataPreset: '',
      dataCustomizada: '',
      nivel: [],
      ordenacao: 'data_asc'
    })
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 z-40"
          />
          
          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="fixed inset-0 flex items-center justify-center p-4 z-50"
          >
            <div className="w-full max-w-3xl max-h-[90vh] bg-background-white rounded-xl shadow-xl overflow-hidden flex flex-col">
              <div className="flex-1 flex flex-col min-h-0">
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-border-primary">
                <div className="flex items-center space-x-2">
                  <Filter className="w-5 h-5 text-brand-primary" />
                  <h2 className="text-lg font-semibold text-text-primary">Filtros</h2>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-background-secondary rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 p-6 space-y-6 overflow-y-auto min-h-0">
                {/* Modalidade */}
                <div>
                  <Body className="font-medium mb-3">Modalidade</Body>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {modalidades.map(modalidade => (
                      <MultiSelectButton
                        key={modalidade}
                        label={modalidade}
                        selected={filters.modalidade.includes(modalidade)}
                        onClick={() => handleModalidadeChange(modalidade)}
                      />
                    ))}
                  </div>
                </div>

                {/* Data */}
                <div>
                  <Body className="font-medium mb-3">Data</Body>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => handleDatePreset('hoje')}
                      className={`p-2 text-sm rounded-lg border transition-colors ${
                        filters.dataPreset === 'hoje'
                          ? 'bg-brand-primary text-white border-brand-primary'
                          : 'bg-background-white border-border-primary hover:bg-background-secondary'
                      }`}
                    >
                      Hoje
                    </button>
                    <button
                      onClick={() => handleDatePreset('amanha')}
                      className={`p-2 text-sm rounded-lg border transition-colors ${
                        filters.dataPreset === 'amanha'
                          ? 'bg-brand-primary text-white border-brand-primary'
                          : 'bg-background-white border-border-primary hover:bg-background-secondary'
                      }`}
                    >
                      Amanhã
                    </button>
                    <button
                      onClick={() => handleDatePreset('esta-semana')}
                      className={`p-2 text-sm rounded-lg border transition-colors ${
                        filters.dataPreset === 'esta-semana'
                          ? 'bg-brand-primary text-white border-brand-primary'
                          : 'bg-background-white border-border-primary hover:bg-background-secondary'
                      }`}
                    >
                      Esta Semana
                    </button>
                    <button
                      onClick={() => handleDatePreset('customizada')}
                      className={`p-2 text-sm rounded-lg border transition-colors ${
                        filters.dataPreset === 'customizada'
                          ? 'bg-brand-primary text-white border-brand-primary'
                          : 'bg-background-white border-border-primary hover:bg-background-secondary'
                      }`}
                    >
                      Data Específica
                    </button>
                  </div>
                  
                  {/* Campo customizado */}
                  {filters.dataPreset === 'customizada' && (
                    <div className="mt-3">
                      <input
                        type="date"
                        value={filters.dataCustomizada || ''}
                        onChange={(e) => setFilters(prev => ({ ...prev, dataCustomizada: e.target.value }))}
                        className="w-full p-2 border border-border-primary rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary"
                      />
                    </div>
                  )}
                </div>

                {/* Nível */}
                <div>
                  <Body className="font-medium mb-3">Nível</Body>
                  <div className="grid grid-cols-2 gap-2">
                    {niveis.map(nivel => (
                      <MultiSelectButton
                        key={nivel}
                        label={nivel}
                        selected={filters.nivel.includes(nivel)}
                        onClick={() => handleNivelChange(nivel)}
                      />
                    ))}
                  </div>
                </div>

                {/* Ordenação */}
                <div>
                  <Body className="font-medium mb-3">Ordenação</Body>
                  <div className="relative">
                    <select
                      value={filters.ordenacao}
                      onChange={(e) => setFilters(prev => ({ ...prev, ordenacao: e.target.value as 'data_asc' | 'data_desc' }))}
                      className="w-full p-2 border border-border-primary rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary appearance-none bg-background-white"
                    >
                      <option value="data_asc">Data (Crescente)</option>
                      <option value="data_desc">Data (Decrescente)</option>
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-text-secondary pointer-events-none" />
                  </div>
                </div>
              </div>
              </div>

              {/* Footer - Fixed */}
              <div className="flex items-center justify-between p-6 border-t border-border-primary bg-background-white">
                <button
                  onClick={resetFilters}
                  className="text-text-secondary hover:text-text-primary transition-colors text-sm"
                >
                  Limpar Filtros
                </button>
                <div className="flex space-x-3">
                  <Button
                    buttonStyle={ButtonStyle.BORDERED}
                    size="sm"
                    onClick={onClose}
                  >
                    Cancelar
                  </Button>
                  <Button
                    variant={ButtonVariant.PRIMARY}
                    size="sm"
                    onClick={handleApplyFilters}
                  >
                    Aplicar Filtros
                  </Button>
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}